/**
 * The V2/V3 boot sequence — content and timing.
 *
 * Lifted out of triggerBoot so the wording, the phase timings and the skip
 * rules can be tested without running a three-second animation. The component
 * owns the DOM and the timers; everything that decides *what* is shown and
 * *when* lives here.
 */

/**
 * What the status line reads before the first tick lands. The original had it
 * sitting in the markup, so the screen opens on it for 200ms — the status line
 * is never blank.
 */
export const BOOT_INITIAL_STATUS = 'INITIALIZING...';

/** The status line above the progress bar, one per tick. */
export const BOOT_STATUSES = [
  'INITIALIZING SECURE CHANNEL...',
  'VERIFYING NODE INTEGRITY...',
  'LOADING CAMPAIGN VAULT...',
  'DECRYPTING OPERATIVE DATA...',
  'ESTABLISHING UPLINK...',
  'READY',
];

export const BOOT_TIMING = {
  /** Progress bar: one tick per 200ms, +17% each. */
  tickMs: 200,
  tickPct: 17,
  /** Phase 2 starts once the bar has filled (~1200ms) plus a beat. */
  terminalStartMs: 1400,
  /** Typing speed, per character. */
  charMinMs: 7,
  charJitterMs: 6,
  /** Pause between finished lines. */
  lineGapMs: 50,
  /*
   * Phase 3 is a four-beat sequence, not one step. The original paced it
   * deliberately and collapsing the beats is what made the rebuild read wrong:
   * the padlock opened and ACCESS GRANTED stamped at the same instant.
   */
  dotMs: 55,          // one dot every 55ms
  dotCount: 8,
  unlockMs: 250,      // …then a beat, and the padlock opens
  stampMs: 300,       // …then another, and the stamp lands with the rings
  /** How long "ACCESS GRANTED" holds before the warp-out. */
  grantedHoldMs: 700,
  /** The warp-out transition. */
  warpMs: 600,
  /** The short warp on a return visit. The full one is the first-run beat. */
  fastWarpMs: 180,
  /**
   * Unconditional teardown. The sequence runs ~3.5s and is capped at 6s, so
   * this only ever fires if a phase throws or a timer is lost — the overlay
   * covers the whole app, so it must never be able to strand the user.
   */
  watchdogMs: 8000,
  /**
   * A return visit in the same browser session. Long enough to read
   * "SESSION RESTORED", short enough that it does not hold the app.
   */
  fastMs: 280,
};

/** sessionStorage flag: this tab has already played the sequence once. */
export const SESSION_BOOTED_KEY = 'affdash.sessionBooted';

/** True only for the exact flag a finished sequence writes. */
export function bootIsRepeat(stored) {
  return stored === '1';
}

/** Whether this tab has already been through the sequence. */
export function readSessionBooted(storage = globalThis.sessionStorage) {
  try {
    return bootIsRepeat(storage?.getItem?.(SESSION_BOOTED_KEY));
  } catch {
    return false;
  }
}

/** Remember that the sequence finished, including when it was skipped. */
export function markSessionBooted(storage = globalThis.sessionStorage) {
  try { storage?.setItem?.(SESSION_BOOTED_KEY, '1'); } catch { /* private mode */ }
}

/** How long a return visit holds the overlay, including its short warp. */
export function bootReturnMs() {
  return BOOT_TIMING.fastMs + BOOT_TIMING.fastWarpMs;
}

/**
 * The terminal lines. The operative's name is interpolated, upper-cased, and
 * falls back to the original's wording when there is no stored name.
 */
export function bootLines(name) {
  const who = String(name || 'operative').toUpperCase();
  return [
    '> establishing secure uplink to node EAST-1 ...',
    '> handshake 0x9F4A ... OK',
    '> verifying operative signature ... OK',
    '> decrypting campaign vault ... OK',
    `> welcome, super affiliate "${who}"`,
    '> clearance level active · profit-share online',
    '> routing to operative console ...',
  ];
}

/**
 * Whether the sequence should play at all.
 *
 * Reduced motion wins over everything: this is a full-screen animation with
 * flashing progress and a warp transition, which is exactly what that setting
 * exists to suppress. V1 is the plain theme and never boots.
 */
export function shouldBoot({ theme, bootEnabled, reducedMotion } = {}) {
  if (reducedMotion) return false;
  if (!bootEnabled) return false;
  return theme === 'v2' || theme === 'v3';
}

/**
 * The progress-bar ticks, as the percentages the bar passes through.
 *
 * Capped at 100 — the raw 17% step overshoots on the seventh tick, and a bar
 * reading "119%" is the kind of detail that makes the rest look unreliable.
 */
export function bootProgress() {
  const steps = [];
  let pct = 0;
  while (pct < 100) {
    pct = Math.min(100, pct + BOOT_TIMING.tickPct);
    steps.push(pct);
  }
  return steps;
}

/** Roughly how long a full run takes — used by the onboarding hand-off. */
export function bootDurationMs(lineCount = bootLines().length) {
  const typing = lineCount * (12 * (BOOT_TIMING.charMinMs + BOOT_TIMING.charJitterMs / 2)
    + BOOT_TIMING.lineGapMs);
  return BOOT_TIMING.terminalStartMs + typing
    + BOOT_TIMING.dotCount * BOOT_TIMING.dotMs
    + BOOT_TIMING.unlockMs + BOOT_TIMING.stampMs
    + BOOT_TIMING.grantedHoldMs + BOOT_TIMING.warpMs;
}
